import { fs } from '@/theme/tokens';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { PlatformCall } from './common';

/**
 * Every workspace's administrators in one list, and a password reset that
 * actually gets them back in.
 *
 * A reset writes a new random password, signs out whoever held the old one,
 * and — for a workspace that normally signs in through uKnowva — can open the
 * login page's Workspace ID field to it. Without that last step the new
 * password is correct and there is no page that will check it.
 *
 * Passwords exist only in the reset response: shown once, never stored.
 */

interface AdminAccount {
  username: string;
  full_name: string;
  email: string;
  is_active: boolean;
  has_password: boolean;
  sso_provider: string | null;
  platform_locked: boolean;
}

interface WorkspaceAdmins {
  tenant_key: string;
  display_name: string;
  origin: 'HRMS' | 'SIGNUP';
  status: string;
  servable: boolean;
  password_login: boolean;
  signin_reachable: boolean;
  login_path: string;
  admins: AdminAccount[];
  error: string | null;
}

export interface ResetTarget {
  tenant_key: string;
  username: string;
  /** Whether the login page can already check a password for this workspace. */
  signin_reachable: boolean;
}

export interface ResetResult {
  tenant_key: string;
  username: string;
  ok: boolean;
  role?: string;
  /** Present only when the server generated it; a typed one is never sent back. */
  password?: string | null;
  password_source?: 'generated' | 'operator';
  login_path?: string;
  signin_reachable?: boolean;
  sessions_ended?: boolean;
  warnings?: string[];
  error?: string;
}

const key = (t: string, u: string) => `${t}\u0000${u}`;

function absolute(path: string | undefined): string {
  if (!path) return '';
  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return path;
  }
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function csvCell(v: string): string {
  // Quote everything, and defuse spreadsheet formulas: a cell that starts
  // with = + - @ is executed by Excel when the file is opened.
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Mirrors the server's signup policy minimum; the server is the real check. */
const MIN_CHOSEN_PASSWORD = 10;

/**
 * Confirm a reset: reason (audited), for a single account whether to generate
 * the password or type one, and — when some target workspace is not open to
 * password sign-in — whether to open it. Used for one account and for a
 * selection alike; a selection always gets generated passwords, one each.
 */
export function ResetPasswordsDialog({
  targets,
  onClose,
  onConfirm,
}: {
  targets: ResetTarget[] | null;
  onClose: () => void;
  /** `newPassword` is set only when the operator typed one. */
  onConfirm: (reason: string, allowPasswordLogin: boolean, newPassword?: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [allow, setAllow] = useState(true);
  const [mode, setMode] = useState<'generate' | 'choose'>('generate');
  const [chosen, setChosen] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const closed = useMemo(
    () => [...new Set((targets ?? []).filter((t) => !t.signin_reachable).map((t) => t.tenant_key))],
    [targets],
  );

  useEffect(() => {
    // Fresh every time it opens — never carry a typed password to the next account.
    if (targets) {
      setReason('');
      setAllow(true);
      setMode('generate');
      setChosen('');
      setReveal(false);
      setErr(null);
    }
  }, [targets]);

  const n = targets?.length ?? 0;
  const choosing = n === 1 && mode === 'choose';
  const chosenTooShort = choosing && chosen.length < MIN_CHOSEN_PASSWORD;
  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(reason.trim(), closed.length > 0 && allow, choosing ? chosen : undefined);
      setChosen('');
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!targets} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {n === 1 ? `Reset the password for ${targets?.[0]?.username ?? ''}?` : `Reset ${n} administrator passwords?`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The old password stops working and anyone signed in on{' '}
          {n === 1 ? 'this account' : 'these accounts'} is signed out.
          {n > 1 && ' Each account gets its own random password, shown to you once.'}
        </Typography>
        {n > 1 && (
          <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 2 }}>
            {targets?.map((t) => `${t.username}@${t.tenant_key}`).join(', ')}
          </Typography>
        )}
        {n === 1 && (
          <Box sx={{ mb: 2 }}>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as 'generate' | 'choose')}
            >
              <FormControlLabel
                value="generate"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Generate a strong password (shown to you once)</Typography>}
              />
              <FormControlLabel
                value="choose"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Set the password myself</Typography>}
              />
            </RadioGroup>
            {choosing && (
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                label="New password"
                type={reveal ? 'text' : 'password'}
                value={chosen}
                onChange={(e) => setChosen(e.target.value)}
                autoComplete="new-password"
                error={chosen.length > 0 && chosenTooShort}
                helperText={`At least ${MIN_CHOSEN_PASSWORD} characters. Not a common password, and not containing the username, email or workspace ID.`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={reveal ? 'Hide password' : 'Show password'}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setReveal((v) => !v)}
                      >
                        {reveal ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        )}
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Recorded in the audit trail with your name"
        />
        {closed.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {closed.length === 1 ? (
              <>Workspace <strong>{closed[0]}</strong> signs in only through uKnowva</>
            ) : (
              <>{closed.length} workspaces ({closed.join(', ')}) sign in only through uKnowva</>
            )}
            , so payroll’s login page cannot check a password for it yet.
            <FormControlLabel
              sx={{ display: 'flex', mt: 1 }}
              control={<Checkbox size="small" checked={allow} onChange={(e) => setAllow(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Also allow password sign-in there (Workspace ID on the login page). uKnowva sign-in
                  keeps working.
                </Typography>
              }
            />
          </Alert>
        )}
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={busy || reason.trim().length < 3 || chosenTooShort}
          onClick={() => void confirm()}
        >
          {busy ? 'Resetting…' : n === 1 ? 'Reset password' : `Reset ${n} passwords`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The one place a new password is ever visible. It does not close on a
 * backdrop click or Escape: losing it means resetting again.
 */
export function ResetResultsDialog({
  results,
  onClose,
}: {
  results: ResetResult[] | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const ok = (results ?? []).filter((r) => r.ok);
  const generated = ok.filter((r) => r.password);
  const failed = (results ?? []).filter((r) => !r.ok);

  const flash = (what: string) => {
    setCopied(what);
    window.setTimeout(() => setCopied(null), 1500);
  };

  const pwText = (r: ResetResult) => r.password ?? '(the password you set)';
  const lines = ok.map(
    (r) => `Workspace ID: ${r.tenant_key}\nUsername: ${r.username}\nPassword: ${pwText(r)}\nSign in at: ${absolute(r.login_path)}`,
  );

  const download = () => {
    const header = ['workspace_id', 'username', 'password', 'sign_in_url', 'warnings'];
    const rows = ok.map((r) => [r.tenant_key, r.username, pwText(r), absolute(r.login_path),
      (r.warnings ?? []).join(' ')]);
    const csv = [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-password-reset-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Dialog open={!!results} fullWidth maxWidth="lg" disableEscapeKeyDown>
      <DialogTitle>
        {ok.length ? `${ok.length} password${ok.length === 1 ? '' : 's'} reset` : 'Nothing was reset'}
        {failed.length > 0 && ` · ${failed.length} failed`}
      </DialogTitle>
      <DialogContent>
        {generated.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            These passwords are shown <strong>once</strong> and are not stored anywhere. Copy or download
            them before closing, and hand each one over privately. Ask the admin to change it after
            signing in.
          </Alert>
        )}
        {ok.length > 0 && generated.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            The password you typed is now set. Hand it over privately and ask the admin to change it
            after signing in.
          </Alert>
        )}
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <TableContainer><Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Workspace ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>New password</TableCell>
                <TableCell>Sign in at</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(results ?? []).map((r) => (
                <TableRow key={key(r.tenant_key, r.username)}>
                  <TableCell>{r.tenant_key}</TableCell>
                  <TableCell>{r.username}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.ok && !r.password ? (
                      <Chip size="small" variant="outlined" label="Set by you" />
                    ) : r.ok ? (
                      <>
                        <code style={{ userSelect: 'all', fontSize: fs.base }}>{r.password}</code>
                        <Tooltip title={copied === key(r.tenant_key, r.username) ? 'Copied' : 'Copy password'}>
                          <IconButton
                            size="small"
                            aria-label={`Copy password for ${r.username}`}
                            onClick={() => void copy(r.password ?? '').then((y) => y && flash(key(r.tenant_key, r.username)))}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Chip size="small" color="error" variant="outlined" label="Not reset" />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.ok && (
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {absolute(r.login_path)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.error && <Typography variant="caption" color="error">{r.error}</Typography>}
                    {(r.warnings ?? []).map((w) => (
                      <Typography key={w} variant="caption" component="div" color="warning.main">{w}</Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
        </Paper>
        {ok.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
            On the login page the admin enters the Workspace ID, their username and this password.
            The sign-in link above fills the Workspace ID in for them.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {ok.length > 0 && (
          <>
            <Button onClick={() => void copy(lines.join('\n\n')).then((y) => y && flash('all'))}>
              {copied === 'all' ? 'Copied' : 'Copy all'}
            </Button>
            <Button onClick={download}>Download CSV</Button>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onClose}>
          {generated.length ? 'I have saved them. Close' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AdminAccessTab({ call }: { call: PlatformCall }) {
  const [data, setData] = useState<WorkspaceAdmins[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [showOff, setShowOff] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [targets, setTargets] = useState<ResetTarget[] | null>(null);
  const [results, setResults] = useState<ResetResult[] | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setData(((await call('/admin-accounts')) as { workspaces: WorkspaceAdmins[] }).workspaces);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const out: { ws: WorkspaceAdmins; a: AdminAccount }[] = [];
    for (const ws of data ?? []) {
      for (const a of ws.admins) {
        if (!showOff && (!a.is_active || a.platform_locked)) continue;
        const hay = `${ws.tenant_key} ${ws.display_name} ${a.username} ${a.full_name} ${a.email}`.toLowerCase();
        if (q && !hay.includes(q)) continue;
        out.push({ ws, a });
      }
    }
    return out;
  }, [data, filter, showOff]);

  const failedWorkspaces = (data ?? []).filter((w) => w.error);
  const emptyWorkspaces = (data ?? []).filter((w) => !w.error && w.admins.length === 0);

  const visibleKeys = rows.map((r) => key(r.ws.tenant_key, r.a.username));
  const allPicked = visibleKeys.length > 0 && visibleKeys.every((k) => picked.has(k));

  const toggle = (k: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const resetTargets = (list: { ws: WorkspaceAdmins; a: AdminAccount }[]): ResetTarget[] =>
    list.map(({ ws, a }) => ({ tenant_key: ws.tenant_key, username: a.username, signin_reachable: ws.signin_reachable }));

  const pickedRows = () => {
    const all: { ws: WorkspaceAdmins; a: AdminAccount }[] = [];
    for (const ws of data ?? []) for (const a of ws.admins) if (picked.has(key(ws.tenant_key, a.username))) all.push({ ws, a });
    return all;
  };

  if (err && !data) return <Alert severity="error">{err}</Alert>;
  if (!data) return <CircularProgress size={24} />;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Administrator accounts in every workspace. Use this when a client’s admin is locked out:
        a reset issues a new password (shown once), signs out the old one, and can open password
        sign-in for workspaces that normally sign in through uKnowva. Each reset is audited.
      </Typography>

      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      {failedWorkspaces.length > 0 && (
        <Alert severity="warning">
          Could not read {failedWorkspaces.length} workspace(s):{' '}
          {failedWorkspaces.map((w) => `${w.tenant_key} (${w.error})`).join('; ')}
        </Alert>
      )}
      {emptyWorkspaces.length > 0 && (
        <Alert severity="info">
          No administrator account in: {emptyWorkspaces.map((w) => w.tenant_key).join(', ')}. Promote a
          user to ADMIN from that workspace’s Users tab.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label="Search workspace or admin"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />}
          label={<Typography variant="body2">Show disabled accounts</Typography>}
        />
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => void load()}>Refresh</Button>
        <Button
          variant="contained"
          color="warning"
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          disabled={picked.size === 0}
          onClick={() => setTargets(resetTargets(pickedRows()))}
        >
          Reset selected ({picked.size})
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <TableContainer><Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPicked}
                  indeterminate={!allPicked && visibleKeys.some((k) => picked.has(k))}
                  onChange={() =>
                    setPicked((p) => {
                      const n = new Set(p);
                      visibleKeys.forEach((k) => (allPicked ? n.delete(k) : n.add(k)));
                      return n;
                    })}
                  inputProps={{ 'aria-label': 'Select all shown' }}
                />
              </TableCell>
              <TableCell>Workspace</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Signs in with</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Password sign-in</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No administrator accounts match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ ws, a }) => {
              const k = key(ws.tenant_key, a.username);
              return (
                <TableRow key={k} hover selected={picked.has(k)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={picked.has(k)} onChange={() => toggle(k)}
                      inputProps={{ 'aria-label': `Select ${a.username} in ${ws.tenant_key}` }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ws.tenant_key}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ws.display_name} · {ws.origin === 'SIGNUP' ? 'Signup' : 'uKnowva'}
                      {!ws.servable && ` · ${ws.status}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{a.username}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.full_name} · {a.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {a.has_password ? 'password' : 'SSO only'}
                      {a.sso_provider ? ` · ${a.sso_provider}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {a.platform_locked ? (
                      <Chip size="small" color="error" variant="outlined" label="Disabled by platform" />
                    ) : (
                      <Chip size="small" variant="outlined" label={a.is_active ? 'Active' : 'Inactive'}
                        color={a.is_active ? 'success' : 'default'} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={ws.signin_reachable
                      ? 'The login page can check a password for this workspace.'
                      : 'Signs in only through uKnowva. A reset can open password sign-in.'}>
                      <Chip size="small" variant="outlined"
                        label={ws.signin_reachable ? 'Open' : 'uKnowva only'}
                        color={ws.signin_reachable ? 'success' : 'default'} />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setTargets(resetTargets([{ ws, a }]))}>
                      Reset password
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table></TableContainer>
      </Paper>

      <ResetPasswordsDialog
        targets={targets}
        onClose={() => setTargets(null)}
        onConfirm={async (reason, allowPasswordLogin, newPassword) => {
          if (!targets) return;
          const body = (await call('/admin-accounts/reset', {
            method: 'POST',
            body: JSON.stringify({
              reason,
              allow_password_login: allowPasswordLogin,
              new_password: newPassword ?? null,
              accounts: targets.map((t) => ({ tenant_key: t.tenant_key, username: t.username })),
            }),
          })) as { results: ResetResult[] };
          setResults(body.results);
          setPicked(new Set());
          void load();
        }}
      />
      <ResetResultsDialog results={results} onClose={() => setResults(null)} />
    </Stack>
  );
}
